// Fresh-draft seed factory. Extracted from `store.openDraft` so the
// store stays under the hand-written file-size limit and the "new draft
// defaults" stay a single cohesive, independently-testable unit. The
// caller mints the `clientToken` and supplies the manager + per-kind
// defaults; everything else is the canonical empty-scratchpad baseline.
//
// @aiPurpose Build the initial `DraftScratchpad` for a never-saved draft
//   opened from the "+" flow.

import { RequestStatus, type RequestKind } from '@magimp/shared';
import type { DraftScratchpad, UserRef } from './types.js';

export interface FreshDraftSeed {
  manager: UserRef;
  clientToken: string;
  destinations: { arrivalCityId: string; sortOrder: number }[];
  kind: RequestKind;
  // WHY (#1599): the lead/deal "Create request" action seeds the pre-bound
  //   funnel link; the standalone "+" omits both so the first save lands an
  //   unlinked request. Carried onto the scratchpad only when present so
  //   `exactOptionalPropertyTypes` keeps the keys genuinely absent for "+".
  leadId?: string | null;
  dealId?: string | null;
}

export function buildFreshDraftScratchpad(seed: FreshDraftSeed): DraftScratchpad {
  return {
    manager: seed.manager,
    // WHY (#1599): include the funnel-link keys only when the seed carried
    //   them so an unlinked "+" scratchpad keeps them absent (not
    //   `undefined`) under `exactOptionalPropertyTypes`.
    ...(seed.leadId !== undefined ? { leadId: seed.leadId } : {}),
    ...(seed.dealId !== undefined ? { dealId: seed.dealId } : {}),
    name: '',
    // WHY (#701): default STANDARD; the "Новый контейнер" button
    //   overrides to 'CONTAINER'. `transportTypeId` is null in both
    //   modes until the user picks a TransportType in the editor.
    kind: seed.kind,
    transportTypeId: null,
    route: { from: 'Китай', to: 'Россия' },
    // WHY (#700): destinations are seeded by the caller (typically from
    //   `useArrivalCities` defaulting to the `isDefault = true` row).
    //   Empty here lets the editor render the empty-state copy while the
    //   catalog fetch resolves; the completeness gate refuses save until
    //   at least one city lands.
    destinations: seed.destinations,
    // WHY (#1057): STANDARD scratchpads carry `null`; the "Новый
    //   контейнер" caller flips `kind: 'CONTAINER'` and the editor
    //   renders the delivery-address field which the user then types
    //   into. Wire layer rejects a non-null value on STANDARD so the
    //   typed default is correct for either kind.
    deliveryAddress: null,
    // WHY (#1393): mirror of `deliveryAddress` — fresh scratchpads seed
    //   `null`; the CONTAINER editor renders the pickup-address field
    //   which the user types into.
    pickupAddress: null,
    // WHY (#1061): fresh scratchpads default insurance to the "not
    //   requested" stance so the create body the save adapter emits
    //   matches the wire's `false → both null` invariant.
    insuranceRequested: false,
    insuranceCargoValue: null,
    insuranceCargoCurrency: null,
    // WHY (#996): fresh scratchpads start with no Roads/Packaging
    //   selections. The user opts in via the multiselect controls below
    //   the cargo section.
    roadOptionIds: [],
    packagingOptionIds: [],
    // WHY (#1448, #2116): STANDARD and CONTAINER request-level totals start
    //   empty; the editor's weight/volume block fills them and the completeness
    //   gate refuses Send until both are > 0. Only SIMPLE never reads these slots.
    totalWeight: null,
    totalVolume: null,
    items: [],
    boxes: [],
    boxesSectionOpen: false,
    clientToken: seed.clientToken,
    // INVARIANT (#536): a never-saved scratchpad is always DRAFT from the
    //   editor's perspective. RETURN can only enter the editor through
    //   `openPersistedDraft`.
    status: RequestStatus.DRAFT,
    returnReason: null,
  };
}
